import {
  BaseDrawer, BaseDrawerTitle, BaseLabel, AvatarCombobox,
} from '@mgm/common/components/base';
import { useTheme } from '@mui/material';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { TCustomTheme } from '@mgm/common/theme';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import Icon from '@mdi/react';
import { BASE_COLORS, getTimezone } from '@mgm/common/utils';
import { mdiAsterisk } from '@mdi/js';
import moment from 'moment-timezone';
import { ChangeEvent, useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { getIsOpenCopyShiftsNav } from '../../../../store/schedules/selectors';
import { schedulesActions } from '../../../../store/schedules/slice';
import { InputDatePicker } from '../../../../components/pickers';
import { TMember } from '../../../../types';
import { getMembers } from '../../../../store/team/selectors';
import { useCopyScheduleAPI } from '../../../../hooks/api/useSchedules';

const CopyScheduleNav = () => {
  const isOpenCopyShiftsNav = useAppSelector(getIsOpenCopyShiftsNav);
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['schedule', 'payroll', 'common']);
  const [copyOption, setCopyOption] = useState('1');
  const members = useAppSelector(getMembers);
  const { mutateAsync: copyScheduleMutate, isLoading } = useCopyScheduleAPI();

  const copyOptions = [
    { name: t('copyShiftsForSelectedEmployee'), value: 0, id: 'employee' },
    { name: t('copyEntireSchedule'), value: 1, id: 'all' },
  ];

  const [formInputs, setFormInputs] = useState({
    startDate: null,
    endDate: null,
    copyTo: null,
    members: [] as Array<TMember>,
  });

  const methods = useForm({
    mode: 'all',
    values: {
      startDate: null,
      endDate: null,
      copyTo: null,
    },
  });

  const resetForm = () => {
    methods.reset({
      startDate: null,
      endDate: null,
      copyTo: null,
    });
    setFormInputs({
      startDate: null,
      endDate: null,
      copyTo: null,
      members: [] as Array<TMember>,
    });
    setCopyOption('1');
  };

  const handleCloseNav = () => {
    resetForm();
    dispatch(schedulesActions.setIsOpenCopyShiftsNav(false));
  };

  const handleFormChange = (key: string, value: unknown) => {
    setFormInputs((prevState) => ({
      ...prevState, [key]: value,
    }));
  };

  const onCopyRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCopyOption(event.target?.value);
  };

  const handleCopyShifts = async () => {
    const data = {
      from: moment.tz(formInputs.startDate, getTimezone()).format() as string,
      to: moment.tz(formInputs.endDate, getTimezone()).format(),
      copyTo: moment.tz(formInputs.copyTo, getTimezone()).format(),
      copyAll: copyOption === '1',
      employeeEmails: formInputs.members?.map((member) => member.email),
    };

    await copyScheduleMutate(data);
    handleCloseNav();
  };

  return (
    <BaseDrawer
      drawerProps={{
        anchor: 'right',
        width: isMobile ? 'auto' : 800,
        variant: 'persistent',
      }}
      open={isOpenCopyShiftsNav}
      onCloseDrawer={handleCloseNav}
    >
      <BaseDrawerTitle handleClose={handleCloseNav} title={t('copySchedule')} subtitle={t('saveTimeByReusing')} />
      <Divider sx={{ borderStyle: 'dashed' }} />
      <FormProvider {...methods}>
        <Box
          paddingRight={isMobile ? 2 : 5}
          paddingLeft={isMobile ? 2 : 5}
          component="form"
          onSubmit={methods.handleSubmit(handleCopyShifts)}
        >
          <Grid paddingTop={2} container spacing={4}>
            <Grid
              pt={2}
              size={{
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
              }}
            >
              <Typography variant="subtitle2">{t('chooseTheDateRangeYouWantToCopy')}</Typography>
            </Grid>
            <Grid
              id="start-date"
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('startDate', { ns: 'timesheets' })}</BaseLabel>
                <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
              </div>
              <InputDatePicker
                placeholder={t('startDate', { ns: 'timesheets' })}
                maxDate={moment()}
                name="startDate"
                isRequired
                onChange={(date) => handleFormChange('startDate', date)}
              />
            </Grid>
            <Grid
              id="end-date"
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('endDate', { ns: 'timesheets' })}</BaseLabel>
                <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
              </div>
              <InputDatePicker
                minDate={formInputs.startDate}
                name="endDate"
                isRequired
                placeholder={t('endDate', { ns: 'timesheets' })}
                onChange={(date) => handleFormChange('endDate', date)}
              />
            </Grid>
            <Grid
              pt={2}
              size={{
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
              }}
            >
              <Divider sx={{ borderStyle: 'dashed' }} />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
              }}
            >
              <Typography variant="subtitle2">{t('chooseTheDateYouWantToCopy')}</Typography>
            </Grid>
            <Grid
              id="copy-to"
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('destinationDate')}</BaseLabel>
                <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
              </div>
              <InputDatePicker
                minDate={formInputs.endDate}
                name="copyTo"
                isRequired
                placeholder={t('destinationDate')}
                onChange={(date) => handleFormChange('copyTo', date)}
              />
              {formInputs.startDate && formInputs.endDate && formInputs.copyTo && (
              <Box pt={2}>
                <Typography color="primary" variant="caption">
                  {`${t('youAreCopying')} ${moment.tz(formInputs.startDate, getTimezone()).format('DD/MM')} - ${moment.tz(formInputs.endDate, getTimezone()).format('DD/MM')} ${t('en')} ${moment.tz(formInputs.copyTo, getTimezone()).format('DD/MM')} - ${moment
                    .tz(formInputs.copyTo, getTimezone())
                    .add(
                      moment(formInputs.endDate).diff(moment(formInputs.startDate), 'days'),
                      'days',
                    )
                    .format('DD/MM')}`}
                </Typography>
              </Box>
              )}
            </Grid>
            <Grid
              pt={2}
              size={{
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
              }}
            >
              <Divider sx={{ borderStyle: 'dashed' }} />
            </Grid>
            <Grid
              size={{
                xs: 12, sm: 12, md: 12, lg: 12,
              }}
            >
              <RadioGroup
                row
                value={copyOption}
                onChange={onCopyRadioChange}
              >
                {copyOptions.map((radio) => (
                  <FormControlLabel
                    key={radio.id}
                    value={radio.value}
                    control={<Radio />}
                    label={radio.name}
                  />
                ))}
              </RadioGroup>
            </Grid>
            {copyOption === '0' && (
            <Grid size={{
              xs: 12, sm: 6, md: 6, lg: 6,
            }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('selectMembers', { ns: 'payroll' })}</BaseLabel>
                <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
              </div>
              <AvatarCombobox
                isDisableMargin
                selectedMembers={formInputs?.members as Array<TMember>}
                members={members}
                onChange={(members) => handleFormChange('members', members)}
                locale={{
                  selectMembers: t('selectMembers', { ns: 'payroll' }),
                  notFound: t('notFound', { ns: 'common' }),
                  noResultsFoundFor: t('noResultsFoundFor', { ns: 'common' }),
                  tryCheckingForTypos: t('tryCheckingForTypos', { ns: 'common' }),
                }}
              />
            </Grid>
            )}
            <Grid
              size={{
                xs: 12,
                lg: 12,
              }}
              paddingTop={4}
            >
              <LoadingButton
                id="submit-shift"
                type="submit"
                loading={isLoading}
                disabled={!methods.formState.isValid}
                size="large"
                variant="contained"
              >
                {t('copyShifts')}
              </LoadingButton>
            </Grid>
          </Grid>
        </Box>
      </FormProvider>
    </BaseDrawer>
  );
};

export {
  CopyScheduleNav,
};
